/*
 * Magic Needles — Saved Wishlist
 * Manual Save & Restore using Shopify Customer Account API.
 */

(() => {
  "use strict";

  const CONFIG = {
    clientId: "bc76562a-76a7-4b8b-ba42-21528b41dd69",
    redirectUri: "https://magicneedles.in/pages/wishlist",
    storageKey: "mn_wishlist_auth"
  };

  let actionBusy = false;

  function setBusy(busy) {
    actionBusy = busy;

    const button = document.getElementById("mn-save-cart");

    if (!button) return;

    button.disabled = busy;
    button.setAttribute("aria-busy", String(busy));
  }

    function showStatus(message) {
    const status = document.getElementById(
        "mn-wishlist-save-status"
    );

    if (status) {
        const isSuccess = message === "Wishlist saved successfully!";

        status.textContent = isSuccess
        ? "✅ Your wishlist has been saved to your account."
        : message;

        status.hidden = false;
    }

    console.info("Magic Needles Wishlist:", message);
    }

  function base64url(bytes) {
    return btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }

  function randomString() {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return base64url(bytes);
  }

  async function createChallenge(verifier) {
    const digest = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(verifier)
    );

    return base64url(new Uint8Array(digest));
  }

  async function discoverAuth() {
    const response = await fetch(
      "/.well-known/openid-configuration"
    );

    if (!response.ok) {
      throw new Error("Authentication discovery failed");
    }

    return response.json();
  }

  async function discoverCustomerApi() {
    const response = await fetch(
      "/.well-known/customer-account-api"
    );

    if (!response.ok) {
      throw new Error("Customer API discovery failed");
    }

    return response.json();
  }

  async function saveWishlist() {
    if (actionBusy) return;

    const wishlist = window.MinimogTheme?.Wishlist;

    if (!wishlist || !wishlist.getSaveableHandles) {
      showStatus("Wishlist is not ready. Please try again.");
      return;
    }

    setBusy(true);

    try {
      const handles = await wishlist.getSaveableHandles();

        // Check the 50-product limit first
        if (wishlist.products.length > 50 || handles.length > 50) {
        showStatus(
            "⚠️ Wishlist limit of 50 products exceeded! Please remove some items before saving."
        );
        return;
        }

        // Then check whether there are products to save
        if (!handles.length) {
        showStatus(
            "Your wishlist is empty. Add products before saving."
        );
        return;
        }

      const verifier = randomString();
      const state = randomString();
      const challenge = await createChallenge(verifier);

        sessionStorage.setItem(
        CONFIG.storageKey,
        JSON.stringify({
            action: "save",
            verifier,
            state,
            handles
        })
        );

      const auth = await discoverAuth();

      const url = new URL(auth.authorization_endpoint);

      url.searchParams.set("client_id", CONFIG.clientId);
      url.searchParams.set("redirect_uri", CONFIG.redirectUri);
      url.searchParams.set("response_type", "code");
      url.searchParams.set(
        "scope",
        "openid email customer-account-api:full"
      );
      url.searchParams.set("state", state);
      url.searchParams.set("code_challenge", challenge);
      url.searchParams.set("code_challenge_method", "S256");

      window.location.assign(url.toString());

    } catch (error) {
      sessionStorage.removeItem(CONFIG.storageKey);
      console.error("Wishlist authentication failed:", error);

      showStatus(
        "We couldn't connect to your account. Please try again."
      );

    } finally {
      setBusy(false);
    }
  }

async function restoreWishlist() {
  if (actionBusy) return;

  const dialog = document.getElementById(
    "mn-cart-restore-dialog"
  );

  const confirmButton = document.getElementById(
    "mn-cart-restore-confirm"
  );

  const cancelButton = document.getElementById(
    "mn-cart-restore-cancel"
  );

  if (!dialog || !confirmButton || !cancelButton) {
    showStatus("Restore is not ready. Please try again.");
    return;
  }

  // Ask before replacing the current wishlist
  const confirmed = await new Promise(resolve => {
    confirmButton.onclick = () => dialog.close("confirm");
    cancelButton.onclick = () => dialog.close("cancel");

    dialog.addEventListener(
      "close",
      () => resolve(dialog.returnValue === "confirm"),
      { once: true }
    );

    dialog.showModal();
  });

  if (!confirmed) return;

  setBusy(true);

  try {
    const verifier = randomString();
    const state = randomString();
    const challenge = await createChallenge(verifier);

    sessionStorage.setItem(
      CONFIG.storageKey,
      JSON.stringify({
        action: "restore",
        verifier,
        state
      })
    );

    const auth = await discoverAuth();
    const url = new URL(auth.authorization_endpoint);

    url.searchParams.set("client_id", CONFIG.clientId);
    url.searchParams.set("redirect_uri", CONFIG.redirectUri);
    url.searchParams.set("response_type", "code");
    url.searchParams.set(
      "scope",
      "openid email customer-account-api:full"
    );
    url.searchParams.set("state", state);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", "S256");

    window.location.assign(url.toString());

  } catch (error) {
    sessionStorage.removeItem(CONFIG.storageKey);

    console.error("Wishlist restore authentication failed:", error);

    showStatus(
      "We couldn't connect to your account. Please try again."
    );

  } finally {
    setBusy(false);
  }
}

async function getSavedWishlistHandles(api, accessToken) {
  const response = await fetch(api.graphql_api, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: accessToken
    },
    body: JSON.stringify({
      query: `query {
        customer {
          metafield(namespace: "custom", key: "saved_wishlist") {
            value
          }
        }
      }`
    })
  });

  if (!response.ok) {
    throw new Error("Saved Wishlist lookup failed");
  }

  const result = await response.json();

  if (result.errors?.length || !result.data?.customer) {
    throw new Error("Unable to retrieve saved Wishlist");
  }

  const value = result.data.customer.metafield?.value;

  if (!value) {
    return [];
  }

  const saved = JSON.parse(value);

  if (
    saved.version !== 1 ||
    !Array.isArray(saved.products)
  ) {
    throw new Error("Invalid saved Wishlist format");
  }

  const handles = [...new Set(
    saved.products.filter(
      handle => typeof handle === "string" && handle.trim()
    )
  )];

  if (handles.length > 50) {
    throw new Error("Saved Wishlist exceeds 50 products");
  }

  return handles;
}

async function validateSavedWishlistHandles(handles) {
  const valid = new Array(handles.length).fill(false);
  let nextIndex = 0;

  const checkNext = async () => {
    while (nextIndex < handles.length) {
      const index = nextIndex++;
      const handle = handles[index];

      const url = formatUrl(
        "products",
        handle,
        "view=grid-card-item"
      );

      // Fetch directly so we can check the HTTP response
      const response = await fetch(url, {
        credentials: "same-origin",
        cache: "no-store"
      });

      // Deleted or unavailable product page
      if (response.status === 404 || response.status === 410) {
        continue;
      }

      // Network/server error: stop restoration
      if (!response.ok) {
        throw new Error(
          `Product validation failed: ${response.status}`
        );
      }

      const html = await response.text();

      const documentFragment = new DOMParser().parseFromString(
        html,
        "text/html"
      );

      // A valid product card is required
      valid[index] = Boolean(
        documentFragment.querySelector(".m-product-card")
      );
    }
  };

  // Validate up to five products simultaneously
  const workers = Array.from(
    { length: Math.min(5, handles.length) },
    () => checkNext()
  );

  // Any network/server failure rejects the entire validation
  await Promise.all(workers);

  // Only return products with valid product cards
  return handles.filter((handle, index) => valid[index]);
}

  async function handleCallback() {
    const url = new URL(window.location.href);

    const code = url.searchParams.get("code");
    const returnedState = url.searchParams.get("state");
    const oauthError = url.searchParams.get("error");

    if (!code && !oauthError) return;

    const stored = sessionStorage.getItem(CONFIG.storageKey);

    sessionStorage.removeItem(CONFIG.storageKey);

    ["code", "state", "error", "error_description", "iss"]
      .forEach(key => url.searchParams.delete(key));

    history.replaceState(
      history.state,
      "",
      url.pathname + url.search + url.hash
    );

    if (!stored) {
      showStatus("Your session expired. Please try saving again.");
      return;
    }

    const {
    action = "save",
    verifier,
    state,
    handles
    } = JSON.parse(stored);

    if (state !== returnedState || oauthError || !code) {
      showStatus("Sign-in wasn't completed. Please try again.");
      return;
    }

    setBusy(true);

    showStatus(
    action === "restore"
        ? "Restoring your saved wishlist…"
        : "Saving your wishlist…"
    );

    try {
      const auth = await discoverAuth();

      const tokenResponse = await fetch(auth.token_endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: new URLSearchParams({
          grant_type: "authorization_code",
          client_id: CONFIG.clientId,
          redirect_uri: CONFIG.redirectUri,
          code,
          code_verifier: verifier
        })
      });

      if (!tokenResponse.ok) {
        throw new Error("Token exchange failed");
      }

      const tokens = await tokenResponse.json();

      if (!tokens.access_token) {
        throw new Error("Access token missing");
      }

      const api = await discoverCustomerApi();

      const queryResponse = await fetch(api.graphql_api, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: tokens.access_token
        },
        body: JSON.stringify({
          query: `query {
            customer {
              id
              metafield(namespace: "custom", key: "saved_wishlist") {
                compareDigest
              }
            }
          }`
        })
      });

      if (!queryResponse.ok) {
        throw new Error("Customer lookup failed");
      }

      const customerResult = await queryResponse.json();

      if (
        customerResult.errors?.length ||
        !customerResult.data?.customer?.id
      ) {
        throw new Error("Customer identification failed");
      }

      const customer = customerResult.data.customer;

      // Carry Shopify-verified authentication through the page reload.
      sessionStorage.setItem(
        "mn_unified_check_result",
        JSON.stringify({
          status: "authenticated",
          createdAt: Date.now()
        })
      );

      // Notify Unified Login after Shopify verifies the customer
      window.MagicNeedlesCustomerSession?.confirmAuthenticated();

      // Remember that Customer Account authentication succeeded in this tab
      sessionStorage.setItem(
        "mn_wishlist_verified_at",
        String(Date.now())
      );

      updateWishlistLoginUI();

      // Restore Saved Wishlist
      if (action === "restore") {
        const progress = document.getElementById(
          "mn-wishlist-restore-progress"
        );

        if (progress) {
          progress.hidden = false;
        }

        // Retrieve saved products from the customer's account
        const savedHandles = await getSavedWishlistHandles(
          api,
          tokens.access_token
        );

        if (!savedHandles.length) {
          showStatus(
            "You don't have a saved wishlist to restore."
          );
          return;
        }

        // Only retain products that still have valid product cards
        const validHandles = await validateSavedWishlistHandles(
          savedHandles
        );

        if (!validHandles.length) {
          showStatus(
            "None of your saved products could be loaded. Your current wishlist has not been changed."
          );
          return;
        }

        // Replace the local Wishlist only after validation
        localStorage.setItem(
          "m-wishlist-products",
          JSON.stringify(validHandles)
        );

        // Display success after the page reloads
        sessionStorage.setItem(
          "mn_wishlist_restore_notice",
          "success"
        );

        window.location.reload();
        return;
      }

      // Save Wishlist

      const snapshot = {
        version: 1,
        updated_at: new Date().toISOString(),
        products: handles
      };

      const saveResponse = await fetch(api.graphql_api, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: tokens.access_token
        },
        body: JSON.stringify({
          query: `mutation SaveWishlist(
            $metafields: [MetafieldsSetInput!]!
          ) {
            metafieldsSet(metafields: $metafields) {
              metafields {
                namespace
                key
              }
              userErrors {
                field
                message
                code
              }
            }
          }`,
          variables: {
            metafields: [{
              ownerId: customer.id,
              namespace: "custom",
              key: "saved_wishlist",
              type: "json",
              value: JSON.stringify(snapshot),
              compareDigest:
                customer.metafield?.compareDigest ?? null
            }]
          }
        })
      });

      if (!saveResponse.ok) {
        throw new Error("Wishlist save request failed");
      }

      const result = await saveResponse.json();

        if (
        result.errors?.length ||
        result.data?.metafieldsSet?.userErrors?.length ||
        !result.data?.metafieldsSet?.metafields?.length
        ) {
        console.error(
            "Saved Wishlist — Shopify errors:",
            result.errors || [],
            result.data?.metafieldsSet?.userErrors || []
        );

        throw new Error("Shopify rejected the Wishlist save");
        }

      showStatus("Wishlist saved successfully!");

        } catch (error) {
        console.error("Saved Wishlist error:", error);

        showStatus(
        action === "restore"
            ? "❌ We couldn't restore your saved wishlist due to a connection or server issue. Your current wishlist has not been changed. Please try again."
            : "We couldn't save your wishlist. Please try again."
        );

        } finally {
        setBusy(false);

        const progress = document.getElementById(
            "mn-wishlist-restore-progress"
        );

        if (progress) {
            progress.hidden = true;
        }
        }
  }

function updateWishlistLoginUI() {
  const panel = document.querySelector(
    ".mn-saved-cart-actions[data-mn-wishlist-logged-in]"
  );

  if (!panel) return;

  const sessionStatus =
    window.MagicNeedlesCustomerSession?.getStatus() ?? "unknown";

  const loggedIn =
    sessionStatus === "authenticated" ||
    (
      sessionStatus === "unknown" &&
      panel.dataset.mnWishlistLoggedIn === "true"
    );

  const description = panel.querySelector(
    ".mn-saved-cart-actions__text p"
  );

  const saveButton = document.getElementById("mn-save-cart");
  const restoreButton = document.getElementById("mn-restore-cart");

  if (!description || !saveButton || !restoreButton) return;

  let products = [];

  try {
    const stored = JSON.parse(
      localStorage.getItem("m-wishlist-products") || "[]"
    );

    products = Array.isArray(stored) ? stored : [];
  } catch (error) {
    console.warn("Unable to read local Wishlist:", error);
  }

  const hasProducts = products.length > 0;

  // An empty Wishlist cannot be saved
  saveButton.hidden = !hasProducts;

  if (hasProducts) {
    saveButton.style.removeProperty("display");
  } else {
    saveButton.style.setProperty("display", "none", "important");
  }

  if (loggedIn) {
    description.textContent =
      "Save your wishlist to your account and restore it whenever you return.";

    saveButton.textContent = "Save Wishlist";
    restoreButton.textContent = "Restore Saved Wishlist";

  } else if (hasProducts) {
    description.textContent =
      "Log in to save your wishlist or restore previously saved products.";

    saveButton.textContent = "Log In to Save Wishlist";
    restoreButton.textContent = "Log In to Restore Wishlist";

  } else {
    description.textContent =
      "Already saved a wishlist? Log in to restore it.";

    restoreButton.textContent = "Log In to Restore Wishlist";
  }
}

        window.mnWishlistSave = saveWishlist;
        window.mnWishlistRestore = restoreWishlist;
        updateWishlistLoginUI();

        // Refresh Wishlist buttons when Unified Login status changes.
        window.MagicNeedlesCustomerSession?.onChange(() => {
          updateWishlistLoginUI();
        });

        // Show the restoration notification after page reload
        if (
        sessionStorage.getItem("mn_wishlist_restore_notice") === "success"
        ) {
        sessionStorage.removeItem("mn_wishlist_restore_notice");

        showStatus(
            "✅ Your saved wishlist has been restored successfully."
        );
        }

        handleCallback().catch(error => {
    console.error("Wishlist callback error:", error);
    setBusy(false);
  });

})();